import { computed, onScopeDispose, reactive, readonly, shallowRef, toValue, watch } from 'vue'
import type { MaybeRefOrGetter } from 'vue'

// The bottom stack is one visual column at the bottom of the page card, built
// from two layers. Bars (PublishBar, page bars such as the campaign Preview as
// bar) live in `BarStack` inside the page card, beneath dialogs. Notifications
// live in `NotificationStack`, a fixed layer above dialogs that sits directly
// on top of the bars by following the geometry `BarStack` publishes here.

// `left`/`width` span the page card; `barsLeft`/`barsWidth` are the narrower,
// centred column the bars actually occupy. `top + height` is the bars' bottom edge.
export type BarStackBox = {
  left: number
  width: number
  top: number
  height: number
  barsLeft: number
  barsWidth: number
}
export type BottomStackLayer = 'bars' | 'notifications'

export const BOTTOM_STACK_TARGETS: Record<BottomStackLayer, string> = {
  bars: 'bar-stack-items',
  notifications: 'notification-stack-items'
}

const transitionClasses = {
  enterActiveClass: 'transition duration-200 ease-out motion-reduce:transition-none',
  enterFromClass: 'translate-y-2 opacity-0',
  leaveActiveClass: 'transition duration-150 ease-in motion-reduce:transition-none',
  leaveToClass: 'translate-y-2 opacity-0'
}
export const stackItemTransition = transitionClasses
export const stackListTransition = {
  ...transitionClasses,
  moveClass: 'transition-transform duration-200 ease-out motion-reduce:transition-none'
}

const barStackBox = shallowRef<BarStackBox | null>(null)
const openOverlays = reactive(new Set<symbol>())
const overlayOpen = computed(() => openOverlays.size > 0)

export const useBottomStack = () => ({
  barStackBox: readonly(barStackBox),
  overlayOpen,
  setBarStackBox: (box: BarStackBox | null) => {
    barStackBox.value = box
  }
})

// Menus and popovers that open upward out of a bar would land under the
// notifications stacked above it, so notifications step aside while one is open.
export function useBottomStackOverlay(open: MaybeRefOrGetter<boolean>) {
  const key = Symbol('bottom-stack-overlay')
  watch(
    () => toValue(open),
    (isOpen) => {
      if (isOpen) {
        openOverlays.add(key)
      } else {
        openOverlays.delete(key)
      }
    },
    { immediate: true }
  )
  onScopeDispose(() => openOverlays.delete(key))
}
